// AccessExpiringCard — Home "Access Expiring Soon" list.
// design ref: home.jsx AccessExpiringCard + styles.css:705–753 (.access-row /
// .bar). Shows active grants expiring within 30 days, soonest first, capped at
// 8. Each row: logo, app/item breadcrumb-style title, urgency label, time bar.
// Clicking a row routes to My Access (where Extend lives).

import { Box, Flex, HStack } from "@chakra-ui/react";
import { TbCheck } from "react-icons/tb";

import AppLogo from "components/ds/AppLogo";
import Bar from "components/ds/Bar";
import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import EmptyState from "components/ds/EmptyState";
import { appDisplayName } from "modules/apps/tenants";
import type { MFSelectableResourceAccess } from "state/members/access_v2/catalogs";
import { accessMetrics, type AccessMetrics } from "../MyAccess/AccessGroup";
import {
  remainingBar,
  remainingLabel,
  urgencyTextColor,
} from "../MyAccess/detailLogic";

// Sub-day labels come from the shared ladder ("5h left", "less than an hour
// left", …); the whole-day form stays compact ("3d left") for this card.
const urgencyLabel = (daysLeft: number): string =>
  daysLeft < 1 ? remainingLabel(daysLeft) : `${Math.round(daysLeft)}d left`;

const ExpiringRow = ({
  access,
  onClick,
}: {
  access: MFSelectableResourceAccess;
  onClick: () => void;
}) => {
  const m: AccessMetrics = accessMetrics(access);
  const sr = access.selectableResource;
  const itemName = appDisplayName({ ...sr, name: sr?.name ?? "Access" });
  const appName = access.appName ?? undefined;
  // The SR's own image is often a legacy Feather sentinel ("FiBox") — ignore
  // those so the logo falls back to clean initials. Prefer the catalog item logo.
  const srLogo =
    sr?.imageUrl && !/^Fi[A-Z]/.test(sr.imageUrl) ? sr.imageUrl : undefined;
  const logoUrl = access.appImageUrl ?? srLogo;

  // Shared with the My Access rows and the grant drawer so the same grant
  // never reads differently on two screens.
  const { pct, tone } = remainingBar(m);

  return (
    <Flex
      align="center"
      gap="md" // 12px
      px="sm" // 8px
      py="md" // 12px
      borderRadius="component.lg"
      cursor="pointer"
      transition="background-color 120ms"
      _hover={{ bg: "bg.hover" }}
      onClick={onClick}
      _notLast={{
        borderBottomWidth: "1px",
        borderBottomStyle: "solid",
        borderBottomColor: "neutral.100",
      }}
    >
      <AppLogo size="md" name={itemName} logoUrl={logoUrl} />
      <Box flex="1" minW={0} display="flex" flexDirection="column" gap="2xs">
        <HStack spacing="2xs" align="baseline" fontSize="smd">
          {appName && (
            <Box as="span" color="neutral.700" whiteSpace="nowrap">
              {appName} /
            </Box>
          )}
          <Box as="span" color="neutral.950" fontWeight={600} isTruncated>
            {itemName}
          </Box>
          <Box
            as="span"
            ml="auto"
            fontSize="xs"
            fontWeight={500}
            whiteSpace="nowrap"
            color={urgencyTextColor(tone)}
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {urgencyLabel(m.daysLeft)}
          </Box>
        </HStack>
        <Box w="100%">
          <Bar value={pct} tone={tone} />
        </Box>
      </Box>
    </Flex>
  );
};

export interface AccessExpiringCardProps {
  access: MFSelectableResourceAccess[];
  onViewAll: () => void;
  onRowClick: (access: MFSelectableResourceAccess) => void;
}

const AccessExpiringCard = ({
  access,
  onViewAll,
  onRowClick,
}: AccessExpiringCardProps) => {
  const expiring = access
    .map((a) => ({ a, m: accessMetrics(a) }))
    .filter(({ m }) => !m.indefinite && m.daysLeft <= 30)
    .sort((x, y) => x.m.daysLeft - y.m.daysLeft)
    .slice(0, 8)
    .map(({ a }) => a);

  return (
    // design ref: home.jsx — `.card` with padding 24px 16px on this card.
    <Card py="1xl" px="lg">
      {/* header gets +8px left to align with the rows' 8px gutter */}
      <Box pl="sm">
        <CardHeader
          title="Access Expiring Soon"
          action={{ label: "View all", onClick: onViewAll }}
        />
      </Box>
      {expiring.length === 0 ? (
        <EmptyState
          icon={<TbCheck />}
          title="Nothing expiring soon"
          body="All your active access is well within its window."
        />
      ) : (
        <Box display="flex" flexDirection="column" gap="sm">
          {expiring.map((a) => (
            <ExpiringRow key={a.id} access={a} onClick={() => onRowClick(a)} />
          ))}
        </Box>
      )}
    </Card>
  );
};

export default AccessExpiringCard;
